import type { HttpCore } from '../http';
import type {
  EnergyPoint,
  EnergyQuery,
  EnergyResponse,
  IntervalPoint,
  IntervalsResponse,
  IsoDate,
  MonthlyEnergyQuery,
  MonthlyEnergyResponse,
} from '../types';

// Wire shapes of sf.api (ARCHITECTURE §4.4): the arrays are called series / samples and energy
// buckets carry `period`; the client normalises them to the `points`/`date` shape the pages use.
type EnergyWire = Omit<EnergyResponse, 'points'> & { series?: (Omit<EnergyPoint, 'date'> & { period: string })[] };
type IntervalsWire = Omit<IntervalsResponse, 'points'> & { samples?: IntervalPoint[] };

/** The chart endpoints of a device: aggregated energy and the 15-minute curve. */
export function createDeviceDataApi(http: HttpCore) {
  return {
    /** Aggregated series from energy_daily: granularity day | month | year over [from, to]. */
    energy: (deviceId: string, query: EnergyQuery) =>
      http
        .get<EnergyWire>(`/devices/${encodeURIComponent(deviceId)}/energy`, {
          query: { granularity: query.granularity, from: query.from, to: query.to },
        })
        .then(
          (r): EnergyResponse => ({
            deviceId,
            granularity: r.granularity,
            from: r.from,
            to: r.to,
            totalProductionWh: r.totalProductionWh,
            points: (r.series ?? []).map(({ period, ...rest }) => ({ date: period, ...rest })),
          }),
        ),
    /**
     * Monthly totals per calendar year — the year-over-year view. The API already returns the shape the
     * chart wants, so this one only stamps the device id onto it.
     */
    monthly: (deviceId: string, query: MonthlyEnergyQuery = {}) =>
      http
        .get<Omit<MonthlyEnergyResponse, 'deviceId'>>(`/devices/${encodeURIComponent(deviceId)}/energy/monthly`, {
          query: { years: query.years, endYear: query.endYear },
        })
        .then(
          (r): MonthlyEnergyResponse => ({
            deviceId,
            availableYears: r.availableYears ?? [],
            years: r.years ?? [],
          }),
        ),
    /** 15-minute power curve for one site-local day. */
    intervals: (deviceId: string, date: IsoDate) =>
      http
        .get<IntervalsWire>(`/devices/${encodeURIComponent(deviceId)}/intervals`, { query: { date } })
        .then(
          (r): IntervalsResponse => ({
            deviceId,
            date: r.date,
            timezone: r.timezone,
            points: r.samples ?? [],
            totals: r.totals,
            intraday: r.intraday,
          }),
        ),
  };
}

export type DeviceDataApi = ReturnType<typeof createDeviceDataApi>;
